import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import Split from 'split.js';
import './EOrderEditWorkspace.css';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';

// ---- client user helpers ----
function getLoggedInUser() {
  try {
    return JSON.parse(localStorage.getItem('currentUser') || localStorage.getItem('user') || '{}') || {};
  } catch { return {}; }
}
function isClientUser() {
  const u = getLoggedInUser();
  const roleStr = [u.role, u.userrole, u.type, u.usertype, u.userType]
    .filter(Boolean).map(v => String(v).toLowerCase()).join(' ');
  const explicitClient = (u.isClient === true) || (u.isclient === true) || (u.client === true) || (u.clientuser === true);
  const explicitInternal = (u.isInternal === true) || (u.internal === true);
  const cid = u.clientid ?? u.clientId ?? u.clientID ?? u.client ?? null;
  const hasClientId = Number.isFinite(Number(cid)) && Number(cid) > 0;
  if (explicitInternal) return false;
  if (explicitClient) return true;
  if (hasClientId) return true;
  return roleStr.includes('client') && !roleStr.includes('internal');
}

const API = import.meta.env.VITE_API_URL;

/* -------- localStorage helpers for split sizes -------- */
const LS_KEYS = {
  vertical: 'eow-sizes-vertical',   // [top, bottom]
  horizontal: 'eow-sizes-horizontal'// [left, middle, right]
};
const readSizes = (key, expectedLen, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr) || arr.length !== expectedLen) return fallback;
    if (arr.some(v => typeof v !== 'number' || !isFinite(v) || v <= 0)) return fallback;
    const sum = arr.reduce((s, n) => s + n, 0);
    if (Math.abs(sum - 100) > 0.1) {
      return arr.map(v => (v / sum) * 100);
    }
    return arr;
  } catch {
    return fallback;
  }
};
const writeSizes = (key, sizes) => {
  try { localStorage.setItem(key, JSON.stringify(sizes)); } catch {}
};

/* -------- small helpers -------- */
function isJsonResponse(r) {
  const ct = r.headers.get('content-type') || '';
  return ct.includes('application/json');
}
async function safeJson(r) {
  if (!r) return null;
  if (r.status === 204) return null;
  if (isJsonResponse(r)) return r.json();
  await r.text();
  return null;
}

// Sanitize to a money string: digits + optional single dot + max 2 decimals
function sanitizeMoney(val) {
  if (val == null) return '';
  let s = String(val);
  // Remove any non-digit or dot characters
  s = s.replace(/[^0-9.]/g, '');
  // Keep only the first dot
  const firstDot = s.indexOf('.');
  if (firstDot !== -1) {
    const before = s.slice(0, firstDot + 1);
    const after = s.slice(firstDot + 1).replace(/\./g, '');
    s = before + after;
  }
  // Limit decimals to 2
  if (s.includes('.')) {
    const [intPart, decPartRaw] = s.split('.');
    const decPart = (decPartRaw || '').slice(0, 2);
    s = decPartRaw !== undefined ? `${intPart}${decPart.length ? '.' + decPart : '.'}` : intPart;
  }
  // Trim leading zeros (keep single zero unless followed by dot)
  if (s.startsWith('0') && s.length > 1 && s[1] !== '.') {
    s = s.replace(/^0+(?=\d)/, '');
  }
  return s;
}

// Prevent typing of characters that are not valid in money values
function preventNonMoneyKeys(e) {
  const ctrl = e.ctrlKey || e.metaKey;
  if (ctrl) return; // allow copy/paste/select-all
  const allowedControl = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Escape', 'Enter'];
  if (allowedControl.includes(e.key)) return;
  if (/^[0-9]$/.test(e.key)) return;
  if (e.key === '.') {
    if (e.currentTarget.value.includes('.')) e.preventDefault();
    return;
  }
  // Block e/E/+/- and any other chars
  e.preventDefault();
}

export default function EOrderEditWorkspace({ eorder, editor }) {
  const eorderId = Number(eorder?.id || eorder?.eorderid || eorder?.ID);

  const IS_CLIENT = useMemo(() => isClientUser(), []);

  // Split.js refs
  const topBlockRef = useRef(null);
  const bottomBlockRef = useRef(null);
  const leftRef = useRef(null);
  const midRef = useRef(null);
  const rightRef = useRef(null);
  const verticalInst = useRef(null);
  const horizontalInst = useRef(null);
  const splitInitedRef = useRef(false);

  // Data state (charges + available fees + read-only items)
  const [charges, setCharges] = useState({ data: [], total: 0 });
  const [fees, setFees] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg]   = useState('');
  // Shipments pane removed; tracking edits live in EOrders Meta fieldset

  // Edit charge modal
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editQty, setEditQty] = useState(1);
  const [editAmt, setEditAmt] = useState('');
  const [editName, setEditName] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  // Header fields: shipping method & shipping account
  const [shippingmethod, setShippingmethod] = useState(eorder?.shippingmethod || '');
  const [shippingaccount, setShippingaccount] = useState(eorder?.shippingaccount || '');

  useEffect(() => {
    setShippingmethod(eorder?.shippingmethod || '');
    setShippingaccount(eorder?.shippingaccount || '');
  }, [eorder?.shippingmethod, eorder?.shippingaccount]);

  // Add custom charge
  const [customName, setCustomName] = useState('');
  const [customQty, setCustomQty]   = useState(1);
  const [customAmt, setCustomAmt]   = useState('');
  const SHOW_CUSTOM_CHARGE_FORM = false;
  // available e-order fees qty map (fee.id -> qty)
  const [feeQty, setFeeQty] = useState({});

  const canLoad = useMemo(() => Number.isFinite(eorderId) && eorderId > 0, [eorderId]);

  /* ------------ loaders (charges / available fees / items) ------------ */
  const refreshCharges = useCallback(async () => {
    if (!canLoad) return;
    try {
      const r = await fetch(`${API}/api/eorders/${eorderId}/charges`);
      if (!r.ok) throw new Error(`GET eordercharges ${r.status}`);
      const json = await safeJson(r);
      setCharges({ data: json?.data || [], total: json?.total || 0 });
    } catch {
      setErrMsg('Failed to load e-order charges.');
      setCharges({ data: [], total: 0 });
    }
  }, [canLoad, eorderId]);

  const refreshFees = useCallback(async () => {
    if (!canLoad) return;
    try {
      const r = await fetch(`${API}/api/eorders/${eorderId}/charges/available`);
      if (!r.ok) throw new Error(`GET clienteorderfees ${r.status}`);
      const json = await safeJson(r);
      setFees(Array.isArray(json) ? json : []);
    } catch {
      setErrMsg('Failed to load available e-order fees.');
      setFees([]);
    }
  }, [canLoad, eorderId]);

  const refreshItems = useCallback(async () => {
    if (!canLoad) return;
    try {
      const r = await fetch(`${API}/api/eorders/${eorderId}/items`);
      if (!r.ok) throw new Error(`GET eorderitems ${r.status}`);
      const json = await safeJson(r);
      setItems(Array.isArray(json) ? json : []);
    } catch {
      setErrMsg('Failed to load e-order items.');
      setItems([]);
    }
  }, [canLoad, eorderId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!canLoad || IS_CLIENT) return; // clients don't need charges/fees/items here
      setLoading(true);
      setErrMsg('');
      await Promise.all([refreshCharges(), refreshFees(), refreshItems()]);
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [canLoad, IS_CLIENT, refreshCharges, refreshFees, refreshItems]);

  /* ------------ Split.js init & persistence ------------ */
  useEffect(() => {
    if (IS_CLIENT) return; // clients see only the left editor; no split
    if (splitInitedRef.current) return;
    if (!topBlockRef.current || !leftRef.current) return;

    const defaultVertical = [60, 40];            // [top, bottom]
    const defaultHorizontal = [34, 33, 33];      // [left editor, mid charges, right available fees]

    if (!bottomBlockRef.current || !midRef.current || !rightRef.current) return;

    const vSizes = readSizes(LS_KEYS.vertical, 2, defaultVertical);
    const hSizes = readSizes(LS_KEYS.horizontal, 3, defaultHorizontal);

    const vertical = Split(
      [topBlockRef.current, bottomBlockRef.current],
      {
        direction: 'vertical',
        sizes: vSizes,
        minSize: [320, 200],
        gutterSize: 6,
        snapOffset: 0,
        onDragEnd: sizes => writeSizes(LS_KEYS.vertical, sizes),
      }
    );
    const horizontal = Split(
      [leftRef.current, midRef.current, rightRef.current],
      {
        sizes: hSizes,
        minSize: [260, 280, 260],
        gutterSize: 6,
        snapOffset: 0,
        onDragEnd: sizes => writeSizes(LS_KEYS.horizontal, sizes),
      }
    );

    verticalInst.current = vertical;
    horizontalInst.current = horizontal;
    splitInitedRef.current = true;

    const onResize = () => {
      try {
        const vs = vertical.getSizes();
        const hs = horizontal.getSizes();
        vertical.setSizes(vs);
        horizontal.setSizes(hs);
        writeSizes(LS_KEYS.vertical, vs);
        writeSizes(LS_KEYS.horizontal, hs);
      } catch {}
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      try { vertical.destroy(); } catch {}
      try { horizontal.destroy(); } catch {}
      verticalInst.current = null;
      horizontalInst.current = null;
      splitInitedRef.current = false;
    };
  }, [IS_CLIENT]);

  /* ------------ Actions ------------ */
  const removeCharge = async (id) => {
    try {
      await fetch(`${API}/api/eorders/${eorderId}/charges/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });
      refreshCharges();
    } catch {}
  };

  const openEdit = (row) => {
    if (!row) return;
    setEditRow(row);
    setEditQty(Number(row.qty || 0) || 1);
    setEditAmt((row.amt ?? row.rowtotal ?? 0));
    setEditName(row.custom || row.chargename || '');
    setEditError('');
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditRow(null);
    setEditSaving(false);
    setEditError('');
  };

  const saveEdit = async () => {
    if (!editRow) return;
    const q = Math.max(0, Number(editQty || 0));
    if (!q) { setEditError('Quantity must be greater than 0.'); return; }
    const payload = { qty: q, amt: Math.max(0, Math.round(Number(editAmt || 0) * 100) / 100) };
    // Allow editing name only for custom charges (chargeid = 0)
    if (Number(editRow.chargeid) === 0) {
      payload.custom = (editName || '').trim() || null;
      if (!payload.custom) { setEditError('Name is required for custom charges.'); return; }
    }

    try {
      setEditSaving(true);
      const r = await fetch(`${API}/api/eorders/${eorderId}/charges/${editRow.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const j = await safeJson(r);
        throw new Error(j?.error || `HTTP ${r.status}`);
      }
      await refreshCharges();
      closeEdit();
    } catch (e) {
      setEditSaving(false);
      setEditError('Failed to save changes.');
    }
  };

  const addFeeCharge = async (fee, qty = 1) => {
    try {
      await fetch(`${API}/api/eorders/${eorderId}/charges`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chargeid: fee.id,
          qty: Number(qty) || 1,
          amt: Math.max(0, Number(fee.amt || 0)),
        }),
      });
      refreshCharges();
    } catch {}
  };

  const addCustom = async () => {
    if (!customName || Number(customQty) <= 0) return;
    try {
      await fetch(`${API}/api/eorders/${eorderId}/charges`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chargeid: 0,
          custom: customName,
          qty: Number(customQty) || 1,
          amt: Math.max(0, Math.round((Number(customAmt) || 0) * 100) / 100),
        }),
      });
      setCustomName(''); setCustomQty(1); setCustomAmt('');
      refreshCharges();
    } catch {}
  };

  const saveHeaderPatch = async (patch) => {
    if (!eorderId) return;
    try {
      await fetch(`${API}/api/eorders/${eorderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
    } catch (e) {
      setErrMsg('Failed to save header change.');
      setTimeout(() => setErrMsg(''), 2500);
    }
  };

  if (!canLoad) {
    return (
      <div className="eow-root">
        <div className="eow-card">
          <div className="eow-scroll">{editor || <b>No order selected.</b>}</div>
        </div>
      </div>
    );
  }

  if (IS_CLIENT) {
    return (
      <div className="eow-root">
        <div className="eow-card">
          <div className="eow-card-head">
            <h3 className="eow-title">Edit E-Order #{eorderId}</h3>
            {errMsg ? <span className="eow-error">{errMsg}</span> : null}
          </div>
          <div className="eow-scroll">
            {editor}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="eow-root">
      {/* TOP (three horizontal panes) */}
      <div ref={topBlockRef} className="eow-vertical">
        {/* LEFT: external editor (passed from EOrders.jsx) */}
        <div ref={leftRef} className="eow-left eow-pane">
          <div className="eow-card">
            <div className="eow-card-head">
              <h3 className="eow-title">Edit E-Order #{eorderId}</h3>
              {errMsg ? <span className="eow-error">{errMsg}</span> : null}
            </div>
            <div className="eow-scroll">
              {editor}
            </div>
          </div>
        </div>

        {/* MIDDLE: eorder charges */}
        <div ref={midRef} className="eow-middle eow-pane">
          <div className="eow-card darker">
            <div className="eow-card-head">
              <h3 className="eow-title">E-Order Charges</h3>
              <div className="eow-total">Total: ${Number(charges.total || 0).toFixed(2)}</div>
            </div>

            <div className="eow-scroll">
              <table className="eow-table eow-charges-table">
                <thead>
                  <tr>
                    <th style={{width: 42}}></th>
                    <th>Qty</th>
                    <th>Charge</th>
                    <th>Amt</th>
                    <th>Row Total</th>
                  </tr>
                </thead>
                <tbody>
                  {charges.data?.length ? charges.data.map(row => (
                    <tr key={row.id}>
                      <td style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                        <button className="mini-btn" title="Edit" onClick={() => openEdit(row)}>
                          <FaEdit size={16} />
                        </button>
                        <button className="mini-btn danger" title="Delete" onClick={() => removeCharge(row.id)}>
                          <FaTrash size={16} />
                        </button>
                      </td>
                      <td style={{ textAlign: 'center' }}>{row.qty}</td>
                      <td>{row.custom || row.chargename}</td>
                      <td>${Number(row.amt).toFixed(2)}</td>
                      <td>${Number(row.rowtotal).toFixed(2)}</td>
                    </tr>
                  )) : <tr><td colSpan={5}>No charges yet.</td></tr>}
                </tbody>
              </table>
            </div>

            {SHOW_CUSTOM_CHARGE_FORM && (
              <fieldset className="eow-fieldset" aria-labelledby="eowCustomLegend">
                <legend id="eowCustomLegend">Add Custom Charge</legend>
                <div className="eow-custom-row">
                  <input className="eow-input" placeholder="Custom charge name"
                        value={customName} onChange={e => setCustomName(e.target.value)} />
                  <div className="eow-field">
                    <label htmlFor="eowCustomQty" className="eow-label">Qty</label>
                    <input id="eowCustomQty" className="eow-input eow-narrow" type="number" min={1}
                          value={customQty} onChange={e => setCustomQty(e.target.value)} />
                  </div>
                  <div className="eow-field">
                    <label htmlFor="eowCustomAmt" className="eow-label">Fee Amt</label>
                    <input
                      id="eowCustomAmt"
                      className="eow-input eow-narrow"
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9]*[.][0-9]{0,2}"
                      placeholder="Amt"
                      value={customAmt}
                      onKeyDown={preventNonMoneyKeys}
                      onChange={(e) => setCustomAmt(sanitizeMoney(e.target.value))}
                    />
                  </div>
                  <button className="btn primary" onClick={addCustom}>Add custom</button>
                </div>
              </fieldset>
            )}
          </div>
        </div>

        {/* RIGHT: available eorder fees (from clienteorderfees) */}
        <div ref={rightRef} className="eow-right eow-pane">
          <div className="eow-card darker">
            <div className="eow-card-head">
              <h3 className="eow-title">Available E-Order Fees</h3>
            </div>
            <div className="eow-scroll">
              <table className="eow-table eow-fees-table">
                <thead>
                  <tr>
                    <th style={{width: 42}}></th>
                    <th style={{width: 72}}>Qty</th>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Default Amt</th>
                  </tr>
                </thead>
                <tbody>
                  {fees.length ? fees.map(f => (
                    <tr key={f.id}>
                      <td>
                        <button
                          className="mini-btn"
                          onClick={() => addFeeCharge(f, feeQty[f.id] ?? 1)}
                          title="Add fee to e-order"
                        >
                          <FaPlus size={16} />
                        </button>
                      </td>
                      <td>
                        <input
                          className="eow-input eow-narrow"
                          type="number"
                          min={1}
                          value={feeQty[f.id] ?? 1}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => setFeeQty(prev => ({
                            ...prev,
                            [f.id]: Math.max(1, Number(e.target.value) || 1)
                          }))}
                          style={{ width: '64px' }}
                        />
                      </td>
                      <td>{f.feename}</td>
                      <td>{f.description || ''}</td>
                      <td>${Number(f.amt).toFixed(2)}</td>
                    </tr>
                  )) : <tr><td colSpan={5}>No available e-order fees.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* BOTTOM: E-Order Items (read-only) */}
      <div ref={bottomBlockRef} className="eow-bottom">
        <div className="eow-card">
          <div className="eow-card-head">
            <h3 className="eow-title">E-Order Items</h3>
          </div>
          <div className="eow-scroll">
            <table className="eow-table eow-items-table">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Description</th>
                  <th>Qty</th>
                </tr>
              </thead>
              <tbody>
                {items.length ? items.map(it => (
                  <tr key={it.id || `${it.sku}-${Math.random()}`}>
                    <td>{it.sku}</td>
                    <td>{it.itemdescription}</td>
                    <td>{it.qty_ordered}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={3}>No items found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {loading && <div style={{position:'absolute', inset:0, pointerEvents:'none'}} aria-hidden="true" />}

      {/* Edit Charge Modal */}
      {editOpen && (
        <div className="assign-modal-overlay" onClick={closeEdit}>
          <div
            className="assign-modal"
            style={{ width: 380 }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); saveEdit(); }
              if (e.key === 'Escape') { e.preventDefault(); closeEdit(); }
            }}
            tabIndex={-1}
          >
            <div className="assign-head" style={{ borderBottom: '1px solid var(--border-color, #3e4654)' }}>
              <h3 style={{ margin: 0 }}>Edit Charge</h3>
              <button className="mini-btn" onClick={closeEdit} aria-label="Close">×</button>
            </div>
            <div className="assign-search" style={{ paddingTop: 10 }}>
              {editError ? (
                <div style={{ color: '#ef4444', marginBottom: 8 }}>{editError}</div>
              ) : null}
              {/* Name (only editable for custom charges) */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
                <label style={{ fontSize: 12, opacity: .9 }}>Name</label>
                {Number(editRow?.chargeid) === 0 ? (
                  <input className="neo-input" value={editName}
                         onChange={(e) => setEditName(e.target.value)} placeholder="Custom name" />
                ) : (
                  <input className="neo-input" value={editRow?.chargename || ''} readOnly />
                )}
              </div>
              {/* Qty + Amt */}
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                  <label style={{ fontSize: 12, opacity: .9 }}>Qty</label>
                  <input className="neo-input" type="number" min={1} value={editQty}
                         onChange={(e) => setEditQty(e.target.value)} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                  <label style={{ fontSize: 12, opacity: .9 }}>Amount</label>
                  <input
                    className="neo-input"
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]*[.][0-9]{0,2}"
                    value={editAmt}
                    onKeyDown={preventNonMoneyKeys}
                    onChange={(e) => setEditAmt(sanitizeMoney(e.target.value))}
                  />
                </div>
              </div>
            </div>
            <div className="assign-actions">
              <button className="btn" onClick={closeEdit} disabled={editSaving}>Cancel</button>
              <button className="btn primary" onClick={saveEdit} disabled={editSaving}>
                {editSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
